import {typedMemo} from "../../../utils/typescript";
import {useCallback, useEffect, useMemo} from "react";
import {computeTotalFractionalWidth} from "./utility";
import {isEqual} from "lodash-es";
import {RowContent} from "./RowContent";
import {VirtualizedTableImpl} from "./impls/Virtualized";
import {SimpleTableImpl} from "./impls/Simple";
import {HeaderContent} from "./HeaderContent";
import {GroupHeaderRowContent} from "./GroupHeaderRowContent";
import {
  ExtractTableContext,
  ExtractTableItem,
  ExtractTableItemKey,
  flattenItemsOrGroups,
  isColumnHidden,
  isColumnRenderable,
  ItemsOrGroups,
  processTableItems,
  SetRowSelectedFunction,
  SetSelectedRowKeysFunction,
  TableConfig,
  TableGroup,
  TableState,
} from "../../../hooks/table";
import {TableRootProps, TableRowProps} from "@chakra-ui/react";

export type TableVerticalAlign = "top" | "middle";
export type TableSize = "spacious" | "condensed";

export const Table = typedMemo(
  <C extends TableConfig>({
    config,
    state,
    context,
    items,
    itemsOrGroups: givenItemsOrGroups,
    virtualized = true,
    verticalAlign = "middle",
    size = "spacious",
    minWidth,
    scrollParent,
    rootProps: rootPropsOverride,
    selectedRowKeys = new Set(),
    onSelectedRowKeysChange,
  }: {
    config: C;
    state: TableState<C>;
    context: ExtractTableContext<C>;

    /** A set of rows that are selected (controlled). */
    selectedRowKeys?: Set<ExtractTableItemKey<C>>;

    /** Called when selected rows have changed (controlled). */
    onSelectedRowKeysChange?: SetSelectedRowKeysFunction<C>;

    /** Whether to use virtualization or not. Default: `true`. */
    virtualized?: boolean;

    /**
     * Whether the contents of the table cells are top or middle aligned.
     * Default: `middle`.
     */
    verticalAlign?: TableVerticalAlign;

    /**
     * Size of the table.
     * Default: `spacious`.
     */
    size?: TableSize;

    /**
     * Minimum width for the table (enables horizontal scrolling when parent has overflow-x: auto).
     * E.g., "2500px"
     */
    minWidth?: string;

    /**
     * When provided, virtualization uses this element as the scroll parent instead of the window.
     * Use this when the table is inside another scrollable container.
     */
    scrollParent?: HTMLElement | null;

    /**
     * Props for the table element itself. A table that is part of a larger
     * widget — the grid a combobox pops up — has to say so on the element a
     * screen reader reaches, which only this reaches.
     */
    rootProps?: TableRootProps;
  } & (
    | {items: ExtractTableItem<C>[]; itemsOrGroups?: undefined}
    | {
        /**
         * Rows already sorted and grouped, drawn as given. For a caller that
         * derives the order itself and needs what is drawn to be exactly that.
         */
        itemsOrGroups: ItemsOrGroups<C>;
        items?: undefined;
      }
  )) => {
    // Destructuring state to make `useMemo` dependency arrays happy.
    const {columnOrder, visibleColumns, groupState, sortState, setSortState, setGroupState} = state;

    const columnIds = useMemo(
      () =>
        columnOrder.filter(
          columnId =>
            config.columns[columnId] != null &&
            isColumnRenderable(config.columns[columnId]) &&
            !isColumnHidden(config.columns[columnId], context) &&
            visibleColumns.has(columnId),
        ),
      [columnOrder, config, context, visibleColumns],
    );

    const totalFractionalWidth = useMemo(
      () => computeTotalFractionalWidth(columnIds.map(columnId => config.columns[columnId])),
      [config.columns, columnIds],
    );

    const itemsOrGroups = useMemo(
      () => givenItemsOrGroups ?? processTableItems(config, context, groupState, sortState, items ?? []),
      [config, context, givenItemsOrGroups, groupState, items, sortState],
    );
    const flatItems = useMemo(() => items ?? flattenItemsOrGroups(itemsOrGroups), [items, itemsOrGroups]);

    const supportsSelection =
      typeof config.enableRowSelection === "function"
        ? config.enableRowSelection(context)
        : (config.enableRowSelection ?? false);

    const selectableRowKeys = useMemo<Set<ExtractTableItemKey<C>>>(() => {
      if (!supportsSelection) return new Set();
      const getRowIsSelectable = config.getRowIsSelectable;
      const selectableItems = getRowIsSelectable
        ? flatItems.filter(item => getRowIsSelectable(item, context))
        : flatItems;
      const selectableItemKeys = selectableItems.map(item => config.extractUniqueKey(item) as ExtractTableItemKey<C>);

      return new Set(selectableItemKeys);
    }, [config, context, flatItems, supportsSelection]);

    const setRowSelected = useCallback<SetRowSelectedFunction<C>>(
      (key, action) => {
        onSelectedRowKeysChange?.(prev => {
          const next = new Set(prev);
          const selected = typeof action === "function" ? action(prev.has(key)) : action;

          if (selected) {
            next.add(key);
          } else {
            next.delete(key);
          }

          return next;
        });
      },
      [onSelectedRowKeysChange],
    );

    // Ensure that if the items change, we remove any selected row keys that are
    // no longer present or selectable.
    useEffect(() => {
      onSelectedRowKeysChange?.(prev => {
        const next = prev.intersection(selectableRowKeys);

        // If the sets are equal, return the previous set to avoid unnecessary
        // re-renders.
        return isEqual(prev, next) ? prev : next;
      });
    }, [config, selectableRowKeys, onSelectedRowKeysChange]);

    const renderHeaderContent = useCallback(
      () => (
        <HeaderContent
          config={config}
          columnIds={columnIds}
          selectableRowKeys={selectableRowKeys}
          selectedRowKeys={selectedRowKeys}
          sortState={sortState}
          totalFractionalWidth={totalFractionalWidth}
          setSelectedRowKeys={onSelectedRowKeysChange}
          setGroupState={setGroupState}
          setSortState={setSortState}
          supportsSelection={supportsSelection}
          size={size}
        />
      ),
      [
        columnIds,
        config,
        selectableRowKeys,
        selectedRowKeys,
        setGroupState,
        onSelectedRowKeysChange,
        setSortState,
        sortState,
        totalFractionalWidth,
        supportsSelection,
        size,
      ],
    );

    const shouldRenderGroupHeaderRow = useCallback(
      (group: TableGroup<C>, depth: number): boolean => {
        if (depth >= groupState.length) return false;
        const groupStateEntry = groupState[depth];

        const columnId = groupStateEntry.columnId;
        if (columnId == null) return false;

        const groupConfig = config.columns[columnId].group;
        if (groupConfig == null) return false;

        return groupConfig.shouldRenderHeaderRow?.(group.value, context) ?? true;
      },
      [config.columns, context, groupState],
    );

    const renderGroupHeaderContent = useCallback(
      (group: TableGroup<C>, depth: number) => {
        return (
          <GroupHeaderRowContent
            config={config}
            context={context}
            groupState={groupState}
            depth={depth}
            group={group}
            numColumns={columnIds.length}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={onSelectedRowKeysChange}
            supportsSelection={supportsSelection}
          />
        );
      },
      [columnIds.length, config, context, groupState, selectedRowKeys, onSelectedRowKeysChange, supportsSelection],
    );

    const renderRowContent = useCallback(
      (item: ExtractTableItem<C>) => (
        <RowContent
          config={config}
          item={item}
          context={context}
          columnIds={columnIds}
          verticalAlign={verticalAlign}
          selectedRowKeys={selectedRowKeys}
          setRowSelected={setRowSelected}
          supportsSelection={supportsSelection}
        />
      ),
      [columnIds, config, context, selectedRowKeys, setRowSelected, verticalAlign, supportsSelection],
    );

    const rootProps = useMemo<TableRootProps>(() => ({size, ...rootPropsOverride}), [size, rootPropsOverride]);
    const rowProps = useMemo<TableRowProps>(() => ({verticalAlign}), [verticalAlign]);

    // The row element is the implementation's, so a config that names or
    // disables a row has to reach it from here.
    const getItemRowProps = useCallback(
      (item: ExtractTableItem<C>): TableRowProps => ({
        ...rowProps,
        id: config.getRowId?.(item, context),
        "aria-disabled": config.getRowIsDisabled?.(item, context) ? true : undefined,
        "aria-selected": config.getRowIsSelected?.(item, context) ? true : undefined,
      }),
      [config, context, rowProps],
    );

    const TableImpl = virtualized ? VirtualizedTableImpl : SimpleTableImpl;

    return (
      <TableImpl
        config={config}
        renderHeaderContent={renderHeaderContent}
        renderRowContent={renderRowContent}
        shouldRenderGroupHeaderRow={shouldRenderGroupHeaderRow}
        renderGroupHeaderContent={renderGroupHeaderContent}
        itemsOrGroups={itemsOrGroups}
        rootProps={rootProps}
        rowProps={rowProps}
        getItemRowProps={getItemRowProps}
        minWidth={minWidth}
        scrollParent={scrollParent}
      />
    );
  },
);
